<template>
  <Dialog :open="showModal" @update:open="(e) => $emit('update:show-modal', e)">
    <DialogContent>
      <div v-if="isLoading" class="flex justify-center">
        <LayupSpinner />
      </div>
      <template v-else>
        <template v-if="showSuccess">
          <DialogHeader class="items-center">
            <SuccessIcon />
            <DialogTitle>We'll Be In Touch Soon!</DialogTitle>
            <DialogDescription>
              Thanks for reaching out. Our support team is reviewing your message and will get back to you within 24
              hours. Keep an eye on your inbox - we'll be in touch soon!
            </DialogDescription>
          </DialogHeader>

          <SectionDivider className="my-4" />

          <DialogFooter>
            <Button @click="onClose">Done</Button>
          </DialogFooter>
        </template>
        <template v-else>
          <DialogHeader>
            <DialogTitle>Contact Us</DialogTitle>
            <DialogDescription>
              Need help or have a question? Reach out to the LayUp team - we're here to assist you with anything you
              need.
            </DialogDescription>
            <span v-if="error !== ''" class="text-sm font-medium text-error-500">{{ error }}</span>
          </DialogHeader>
          <div class="py-4">
            <div class="flex flex-col gap-4">
              <!-- Empty divs are to make sure errors aren't effected by gap-4. Don't remove -->
              <div>
                <TextInput
                  id="contactFormName"
                  label="Name"
                  placeholder="John Doe"
                  test-id="contact_form_name"
                  :disabled="isLoading"
                  v-model:input="formValues.name"
                />
              </div>
              <div>
                <TextInput
                  id="contactFormEmail"
                  label="Email"
                  placeholder="john.doe@example.com"
                  :regex="emailRegex"
                  test-id="contact_form_email"
                  :disabled="isLoading"
                  v-model:input="formValues.email"
                />
              </div>
              <div>
                <SelectInput_v2
                  label="Subject"
                  :hide-label="false"
                  :items="['General Customer Service', 'Suggestions', 'Product Support']"
                  v-model="formValues.subject"
                  test-id="contact_form_subject"
                />
              </div>
              <div>
                <TextBoxInput
                  label="Message"
                  v-model="formValues.message"
                  :hide-label="false"
                  test-id="contact_form_message"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" class="w-1/2" @click="onClose"> Cancel </Button>
            <Button
              type="button"
              class="w-1/2"
              data-testid="contact_form_submit"
              :disabled="!fieldsValid"
              @click="onSubmit"
            >
              Send Message
            </Button>
          </DialogFooter>
        </template>
      </template>
    </DialogContent>
  </Dialog>
</template>
<script setup lang="ts">
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { NetworkError } from '@/errors';
import { sendEmail } from '@/services/emails';
import { useMerchantStore } from '@/stores/merchants';
import { useUserStore } from '@/stores/users';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { computed, reactive, ref, watch } from 'vue';
import LayupSpinner from '../common/LayupSpinner.vue';
import SectionDivider from '../common/SectionDivider.vue';
import SuccessIcon from '../icons/SuccessIcon.vue';
import { Button } from '../ui/button';
import SelectInput_v2 from './SelectInput_v2.vue';
import TextBoxInput from './TextBoxInput.vue';
import TextInput from './TextInput.vue';

const props = defineProps<{ showModal: boolean }>();

const emit = defineEmits(['update:show-modal']);

const userStore = useUserStore();
const merchantStore = useMerchantStore();
const { user } = userStore;

const isLoading = ref(false);
const error = ref('');
const showSuccess = ref(false);

const fieldsValid = computed(() => {
  return (
    formValues.name.trim() !== '' &&
    emailRegex.test(formValues.email.trim()) &&
    formValues.subject.trim() !== '' &&
    formValues.message.trim() !== ''
  );
});

const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

const initialFormValues = {
  name: '',
  email: '',
  subject: 'General Customer Service' as const,
  message: '',
  merchantName: '',
  merchantId: '',
  type: 'contact',
};

const formValues = reactive({ ...initialFormValues });

const populateForm = () => {
  formValues.name = user.name ?? '';
  formValues.email = user.email ?? '';
  formValues.merchantName = merchantStore.merchant.name ?? '';
  formValues.merchantId = merchantStore.merchant._id ?? '';
};

const resetForm = () => {
  Object.assign(formValues, initialFormValues);
};

watch(
  () => props.showModal,
  (newValue) => {
    if (newValue) {
      resetForm();
      populateForm();
    }
  },
  { immediate: true },
);

const onClose = () => {
  showSuccess.value = false;
  emit('update:show-modal', false);
};

const onSubmit = async () => {
  error.value = '';
  if (!fieldsValid.value) {
    error.value = 'Please fill in all fields before submitting.';
  }

  try {
    isLoading.value = true;
    await sendEmail({ ...formValues });
    showSuccess.value = true;
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric(`contact_us_modal`, { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric(`contact_us_modal`, { status: 'error', channel: 'merchant' });
    }
  } finally {
    isLoading.value = false;
  }
};
</script>
